import { useEffect, useId, useRef, useState } from 'react';
import { CircleHelp } from 'lucide-react';

/** Hover/focus help that can also be pinned by tapping on touch screens. */
export function ProfileHelp({ label, message }: { label: string; message: string }) {
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  const pinned = useRef(false);
  const hovered = useRef(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const dismiss = () => { pinned.current = false; setOpen(false); };
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) dismiss();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      // First Escape dismisses help; the next can close account settings.
      event.stopPropagation();
      dismiss();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape, true);
    };
  }, [open]);

  return <span className="account-profile-help" ref={root}
    onMouseEnter={() => { hovered.current = true; setOpen(true); }}
    onMouseLeave={() => {
      hovered.current = false;
      if (!pinned.current && !root.current?.querySelector('button:focus-visible')) setOpen(false);
    }}>
    <button type="button" aria-label={label} aria-expanded={open} aria-controls={id}
      aria-describedby={open ? id : undefined}
      onFocus={event => { if (event.currentTarget.matches(':focus-visible')) setOpen(true); }}
      onBlur={() => { pinned.current = false; if (!hovered.current) setOpen(false); }}
      onClick={() => { pinned.current = !pinned.current; setOpen(pinned.current); }}>
      <CircleHelp aria-hidden="true" />
    </button>
    {open && <span className="account-profile-tooltip" id={id} role="tooltip">{message}</span>}
  </span>;
}
